import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { fetchTeamByTeamIdAndSeason } from '../services/TeamService';
import { Team } from '../models/Team';
import { Match } from '../models/Match';
import { mapMappings } from '../Constants';
import useLocalStorage from 'use-local-storage';

interface MatchTableProps {
  matches: Match[];
  compact: boolean;
  season?: number;
  predictMode?: boolean;
  highlightPlayers?: string[]; // New prop for highlighting specific players
}

const MatchTable: React.FC<MatchTableProps> = ({ 
  matches, 
  compact, 
  season, 
  predictMode = false,
  highlightPlayers = [] // Default to empty array
}) => {
  const [teams, setTeams] = useState<{ [key: string]: Team }>({});
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isDark] = useLocalStorage<boolean>('isDark', false);
  const location = useLocation();

  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'ascending' | 'descending';
  }>({
    key: 'date',
    direction: 'descending',
  });

  // Keep track of window resizing to handle mobile layout changes
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // Collect all unique team IDs from the matches that we haven't already fetched
    const teamIdsToFetch: string[] = matches.reduce((ids: string[], match) => {
      if (!teams[match.team1.teamId]) {
        ids.push(match.team1.teamId);
      }
      if (!teams[match.team2.teamId]) {
        ids.push(match.team2.teamId);
      }
      return ids;
    }, []);

    if (teamIdsToFetch.length === 0) {
      return; // Nothing to fetch
    }

    const fetchTeams = async (teamIds: string[]) => {
      try {
        const fetchedTeams: { [key: string]: Team } = {};
        await Promise.all(
          teamIds.map(async (teamId) => {
            if (!teams[teamId]) {
              // If `season` is provided, use that; otherwise, fallback to match's season
              const effectiveSeason = season ?? matches[0]?.season;

              const team = await fetchTeamByTeamIdAndSeason(teamId, effectiveSeason);
              if (team) {
                fetchedTeams[teamId] = team;
              }
            }
          })
        );
        setTeams((prevTeams) => ({ ...prevTeams, ...fetchedTeams }));
      } catch (error) {
        console.error('Error fetching teams:', error);
      }
    };

    fetchTeams(teamIdsToFetch);
  }, [matches, teams, season]);

  // Sorting
  const handleSort = (key: string) => {
    let direction: 'ascending' | 'descending' = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  // Sort the matches array using the chosen config
  const sortedMatches = useMemo(() => {
    const sorted = [...matches];
    if (sortConfig.key) {
      sorted.sort((a, b) => {
        let aValue: any;
        let bValue: any;

        switch (sortConfig.key) {
          case 'date':
            aValue = new Date(a.match_time).getTime();
            bValue = new Date(b.match_time).getTime();
            break;
          case 'team1':
            aValue = teams[a.team1.teamId]?.teamName || '';
            bValue = teams[b.team1.teamId]?.teamName || '';
            break;
          case 'score':
            aValue = a.team1score - a.team2score; // net difference
            bValue = b.team1score - b.team2score;
            break;
          case 'team2':
            aValue = teams[a.team2.teamId]?.teamName || '';
            bValue = teams[b.team2.teamId]?.teamName || '';
            break;
          case 'map':
            aValue = a.mapName;
            bValue = b.mapName;
            break;
          default:
            return 0;
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sorted;
  }, [matches, sortConfig, teams]);

  /**
   * Helper: Build link for a team. If we have a `season`, append ?season=...
   */
  const buildTeamLink = (teamId: string) => {
    if (season !== undefined) {
      return `/team/${teamId}?season=${season}`;
    }
    return `/team/${teamId}`;
  };
  
  /**
   * Create match detail link with returnURL parameters
   */
  const buildMatchLink = (matchId: string) => {
    // Check if we're in the player page context by looking for steamId in the URL
    const currentPath = location.pathname;
    const pathParts = currentPath.split('/');
    const isPlayerPage = pathParts[1] === 'player' && pathParts.length >= 3;
    
    if (isPlayerPage) {
      const steamId = pathParts[2];
      // Get the current tab and season to return to
      const params = new URLSearchParams(location.search);
      const currentTab = params.get('tab') || 'matches'; // Default to matches tab
      const currentSeason = params.get('season') || season?.toString();
      
      // Build the return URL with proper tab and season
      let returnUrl = `/player/${steamId}?tab=${currentTab}`;
      if (currentSeason) {
        returnUrl += `&season=${currentSeason}`;
      }
      
      // Encode the return URL and add it to the match link
      return `/match/${matchId}?returnUrl=${encodeURIComponent(returnUrl)}`;
    }
    
    return `/match/${matchId}`;
  };

  // Helper function to check if a match contains any of the highlighted players
  const shouldHighlightMatch = (match: Match) => {
    if (!highlightPlayers || highlightPlayers.length === 0) return false;
    
    // Check team1 players
    const team1HasHighlightedPlayer = match.team1playerids?.some(playerId => 
      highlightPlayers.includes(playerId)
    );
    
    // Check team2 players
    const team2HasHighlightedPlayer = match.team2playerids?.some(playerId => 
      highlightPlayers.includes(playerId)
    );
    
    // Highlight if any team has a highlighted player
    return team1HasHighlightedPlayer || team2HasHighlightedPlayer;
  };

  // If no matches, show a message
  if (sortedMatches.length === 0) {
    return (
      <div className="container text-center">
        <h1>Ingen kampe fundet</h1>
      </div>
    );
  }

  // Column configuration - similar to UpcomingMatchTable
  const columnWidths = {
    date: '17.5%',
    team1: predictMode ? '40%' : (isMobile ? '35%' : '25%'),
    score: predictMode ? '20%' : (isMobile ? '30%' : '15%'),
    team2: predictMode ? '40%' : (isMobile ? '35%' : '25%'),
    map: '17.5%'
  };

  // Render the table
  return (
    <div className="text-center">
      <table
        className={`table table-striped ${isDark ? 'table-dark' : ''}`}
        style={{
          ...isDark
            ? { 
                border: '1px solid #111', 
                borderCollapse: 'collapse' as 'collapse',
                fontSize: isMobile || predictMode ? '0.85rem' : '1rem',
                tableLayout: 'fixed', // Force fixed layout
                maxWidth: '100%'      // Prevent expanding beyond container
              }
            : { 
                fontSize: isMobile || predictMode ? '0.85rem' : '1rem',
                tableLayout: 'fixed', // Force fixed layout
                maxWidth: '100%'      // Prevent expanding beyond container
              }
        }}
      >
        <thead>
          <tr>
            {/* Date column - hide in compact or predict mode */}
            {!isMobile && !compact && !predictMode && (
              <th
                style={{ 
                  width: columnWidths.date, 
                  cursor: 'pointer' 
                }}
                onClick={() => handleSort('date')}
              >
                Dato{" "}
                {sortConfig.key === 'date' &&
                  (sortConfig.direction === 'ascending' ? '▲' : '▼')}
              </th>
            )}
            <th
              style={{ 
                width: columnWidths.team1, 
                cursor: 'pointer',
                padding: isMobile || predictMode ? '0.5rem' : undefined
              }}
              onClick={() => handleSort('team1')}
            >
              Hold 1{" "}
              {sortConfig.key === 'team1' &&
                (sortConfig.direction === 'ascending' ? '▲' : '▼')}
            </th>
            <th
              style={{ 
                width: columnWidths.score, 
                cursor: 'pointer',
                padding: isMobile || predictMode ? '0.5rem' : undefined
              }}
              onClick={() => handleSort('score')}
            >
              Score{" "}
              {sortConfig.key === 'score' &&
                (sortConfig.direction === 'ascending' ? '▲' : '▼')}
            </th>
            <th
              style={{ 
                width: columnWidths.team2, 
                cursor: 'pointer',
                padding: isMobile || predictMode ? '0.5rem' : undefined
              }}
              onClick={() => handleSort('team2')}
            >
              Hold 2{" "}
              {sortConfig.key === 'team2' &&
                (sortConfig.direction === 'ascending' ? '▲' : '▼')}
            </th>
            {/* Map column - hide in predict mode */}
            {!isMobile && !predictMode && (
              <th
                style={{ width: columnWidths.map, cursor: 'pointer' }}
                onClick={() => handleSort('map')}
              >
                Map{" "}
                {sortConfig.key === 'map' &&
                  (sortConfig.direction === 'ascending' ? '▲' : '▼')}
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {sortedMatches.map((match) => {
            // Check if this match should be highlighted
            const isHighlighted = shouldHighlightMatch(match);
            
            // Get highlighted player positions for this match
            const team1HighlightedPlayers = highlightPlayers ? 
              match.team1playerids?.filter(playerId => highlightPlayers.includes(playerId)) : [];
            const team2HighlightedPlayers = highlightPlayers ? 
              match.team2playerids?.filter(playerId => highlightPlayers.includes(playerId)) : [];
            
            return (
              <tr 
                key={match.matchid} 
                style={{ 
                  height: isMobile ? '70px' : predictMode ? '60px' : '80px',
                  cursor: predictMode ? 'pointer' : undefined,
                  // Add highlight style if needed
                  backgroundColor: isHighlighted ? (isDark ? 'rgba(241, 92, 37, 0.2)' : 'rgba(241, 92, 37, 0.15)') : undefined
                }}
              >
                {/* Date/time (if not mobile & not compact & not predict mode) */}
                {!isMobile && !compact && !predictMode && (
                  <td className="align-middle">
                    {new Date(match.match_time).toLocaleDateString('da-DK', {
                      day: 'numeric',
                      month: 'long',
                    }) +
                      ', ' +
                      new Date(match.match_time).toLocaleTimeString('da-DK', {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false,
                      })}
                  </td>
                )}

                {/* Team 1 link */}
                <td className="align-middle" style={{ padding: isMobile || predictMode ? '0.5rem' : undefined }}>
                  <Link
                    to={buildTeamLink(match.team1.teamId)}
                    className={`text-decoration-none ${
                      isDark ? 'text-light' : 'text-black'
                    } d-flex flex-column align-items-center`}
                  >
                    <div style={{ 
                      width: isMobile ? 30 : predictMode ? 25 : 40, 
                      height: isMobile ? 30 : predictMode ? 25 : 40 
                    }}>
                      <img
                        src={teams[match.team1.teamId]?.teamLogo}
                        alt={teams[match.team1.teamId]?.teamName}
                        style={{ 
                          width: '100%', 
                          height: '100%', 
                          objectFit: 'contain' 
                        }}
                        loading="lazy"
                      />
                    </div>
                    <span className="mt-1 text-truncate" style={{ 
                      maxWidth: '100%',
                      // Add visual indicator if this team has highlighted players
                      fontWeight: team1HighlightedPlayers?.length ? 'bold' : undefined
                    }}>
                      {teams[match.team1.teamId]?.teamName}
                      {team1HighlightedPlayers?.length ? ' ★' : ''}
                    </span>
                  </Link>
                </td>

                {/* Score */}
                <td className="align-middle text-center" style={{ padding: isMobile || predictMode ? '0.5rem' : undefined }}>
                  <div style={{ 
                    fontSize: isMobile ? '1.2rem' : predictMode ? '1rem' : '1.5rem',
                    marginBottom: isMobile ? '0.3rem' : predictMode ? '0.2rem' : '0.5rem'
                  }}>
                    <span className={match.team1won ? 'text-success' : 'text-danger'}>
                      {match.team1score}
                    </span>
                    <span className="mx-2">-</span>
                    <span className={match.team1won ? 'text-danger' : 'text-success'}>
                      {match.team2score}
                    </span>
                  </div>
                  {/* Match detail link - smaller in predict mode */}
                  <Link 
                    to={buildMatchLink(match.matchid)} 
                    className={`btn btn-primary ${isMobile || predictMode ? 'btn-sm' : ''}`}
                    style={predictMode ? { fontSize: '0.75rem', padding: '0.15rem 0.4rem' } : undefined}
                  >
                    {isMobile || predictMode ? 'Se' : 'Se kamp'}
                  </Link>
                </td>

                {/* Team 2 link */}
                <td className="align-middle" style={{ padding: isMobile || predictMode ? '0.5rem' : undefined }}>
                  <Link
                    to={buildTeamLink(match.team2.teamId)}
                    className={`text-decoration-none ${
                      isDark ? 'text-light' : 'text-black'
                    } d-flex flex-column align-items-center`}
                  >
                    <div style={{ 
                      width: isMobile ? 30 : predictMode ? 25 : 40, 
                      height: isMobile ? 30 : predictMode ? 25 : 40 
                    }}>
                      <img
                        src={teams[match.team2.teamId]?.teamLogo}
                        alt={teams[match.team2.teamId]?.teamName}
                        style={{ 
                          width: '100%', 
                          height: '100%', 
                          objectFit: 'contain' 
                        }}
                        loading="lazy"
                      />
                    </div>
                    <span className="mt-1 text-truncate" style={{ 
                      maxWidth: '100%',
                      // Add visual indicator if this team has highlighted players
                      fontWeight: team2HighlightedPlayers?.length ? 'bold' : undefined
                    }}>
                      {teams[match.team2.teamId]?.teamName}
                      {team2HighlightedPlayers?.length ? ' ★' : ''}
                    </span>
                  </Link>
                </td>

                {/* Map info (if not mobile and not predict mode) */}
                {!isMobile && !predictMode && (
                  <td className="align-middle">
                    {match.bo3 ? 'BO3' : mapMappings.get(match.mapName) || match.mapName}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default MatchTable;